import { HISTORY_KIND_WORDS, type ClientProcessDto } from '@nsb/core';
import { useMemo, useState, type ReactNode } from 'react';
import { clientTiles, type ClientCard as Card } from '../tree/client.ts';
import { FOOT } from '../tree/look.ts';
import { stepName } from '../tree/model.ts';
import { BandStrip, Tick } from '../tree/Tile.tsx';
import { TreeView } from '../tree/TreeView.tsx';
import { dateWords } from '../../days.ts';
import { FileChip } from './StepLog.tsx';

/**
 * A process to be read, not worked (Jason, 2026-10-05: "i don't really want the tree to be pan/zoon in the client page,
 * it should just go down the page easy to view"), drawn as the master is (design study 12, 2026-10-06: "the client view
 * of the tree ... to all use the same styles that our master has been iterating on"): each phase a block, its steps
 * as tiles, every line faint; the band in the client's words (You, NSB, Others; client band A), where each stands at
 * its foot (states A). A press opens a step with its chain alone and its card in the middle (open A): what it is and
 * its tasks, ticked as they are done (client tasks A), and, when it is theirs and reached, that it waits on them. On a
 * phone, the phases as blocks of rows. Nothing is dragged, panned, zoomed or added. Drawn as every tree is (TreeView).
 */

/**
 * A step opened, as the client reads it: where it stands, what it is, its tasks, and what NSB shared of its log (design
 * study 31, slice 3). When it is theirs and reached, where
 * they act on it, said to the reader: the client is sent to their Waiting on you, NSB told it waits on the client.
 */
export function ClientCard({ card, staff, projectId }: { card: Card; staff: boolean; projectId: string }) {
  const yours = card.standing === 'yours';
  const act = card.kind === 'gate' ? 'approve it' : card.kind === 'decision' ? 'decide it' : 'mark it done';
  return (
    <div>
      <BandStrip band={card.band} className="rounded-t-[1px]" />
      <div className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-4 px-5 pb-2 pt-4">
        {card.picture ? <img src={card.picture} alt="" className="h-24 w-24 object-contain" /> : <span />}
        <div className="grid gap-1.5">
          <h3 className="font-display text-[1.625rem] font-normal leading-tight">{stepName(card.title)}</h3>
          <p className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.06em] ${FOOT[card.standing]}`}>
            {card.standing === 'done' ? <Tick /> : null}
            {card.foot}
          </p>
        </div>
      </div>
      <div className="grid gap-3 px-5 pb-4">
        {yours ? (
          <p className="border-l-2 border-alert pl-3 text-[0.9375rem]">
            {staff ? `It waits on the client: they ${act} under Waiting on you, on their home.` : `It waits on you: ${act} under Waiting on you, at the top of this page.`}
          </p>
        ) : null}
        {card.what ? (
          <div className="grid gap-1 border-t border-rule pt-2">
            <span className="label">What it is</span>
            <p className="text-[0.9375rem]">{card.what}</p>
          </div>
        ) : null}
        {card.tasks.length ? (
          <div className="grid gap-1 border-t border-rule pt-2">
            <span className="label">
              Its tasks · <span className="num">{card.tasks.length}</span>
            </span>
            <ul className="grid gap-1">
              {card.tasks.map((t) => (
                <li key={t.id} className={`flex items-baseline gap-2 text-[0.9375rem] ${t.done ? 'text-moss' : ''}`} style={{ paddingLeft: t.depth * 16 }}>
                  {t.done ? <Tick /> : <span aria-hidden="true" className="inline-block h-3 w-3 shrink-0 rounded-[2px] border border-ink-3" />}
                  <span>
                    {stepName(t.title)}
                    {t.word ? <span className="text-ink-3"> · {t.word}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {card.log.length ? (
          <div className="grid gap-1 border-t border-rule pt-2">
            <span className="label">Log</span>
            <ul className="grid gap-2">
              {card.log.map((e) => (
                <li key={e.id} className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 text-[0.9375rem]">
                  <span className="font-mono text-[11px] uppercase leading-5 tracking-[0.06em] text-ink-3">
                    {dateWords(e.happenedOn)}
                    <br />
                    {HISTORY_KIND_WORDS[e.kind]}
                  </span>
                  <span>
                    {e.title}
                    {e.body ? <span className="block whitespace-pre-line text-ink-2">{e.body}</span> : null}
                    {e.files.length ? (
                      <span className="block">
                        {e.files.map((f) => (
                          <FileChip key={f.id} projectId={projectId} file={f} />
                        ))}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * `staff`: NSB reads it, on its own home, drawn as the client sees it. `panel`: what a step opens to instead of the
 * client's card (NSB's home: the step's own panel, where it is worked).
 */
export function TreeSheet({
  dto,
  projectId,
  label,
  staff = false,
  panel,
  open: given,
  onOpen,
}: {
  dto: ClientProcessDto;
  projectId: string;
  label: string;
  staff?: boolean;
  panel?: (stepId: string, close: () => void) => ReactNode;
  /** The step opened, held by the page: Working on opens a step here (design study 31). Without it, the sheet holds its own. */
  open?: string | null;
  onOpen?: (stepId: string | null) => void;
}) {
  const [own, setOwn] = useState<string | null>(null);
  const controlled = given !== undefined && onOpen !== undefined;
  const open = controlled ? given : own;
  const setOpen = controlled ? onOpen : setOwn;
  const { phases, steps, cards } = useMemo(() => clientTiles(dto), [dto]);
  return (
    <TreeView
      phases={phases}
      steps={steps}
      label={label}
      open={open && cards.has(open) ? open : null}
      onOpen={setOpen}
      card={(k) => {
        const c = cards.get(k);
        if (!c) return null;
        return panel ? panel(k, () => setOpen(null)) : <ClientCard card={c} staff={staff} projectId={projectId} />;
      }}
      whole="the whole process"
    />
  );
}
